import React from "react";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import AuditCta from "@/components/AuditCta";
import ClosingCta from "@/components/sections/ClosingCta";

const INCLUDED = [
  "Target customer profile and buyer roles, agreed in writing.",
  "Enriched list of 500 contacts for one market, with verified email, phone and LinkedIn.",
  "Sending setup: domains, inboxes, warm-up and deliverability checks.",
  "Email and LinkedIn sequences, written and loaded.",
  "Reply handling and meeting booking into your calendar and CRM.",
  "A written guide to run and adjust the engine.",
];

const TIMELINE = [
  {
    when: "Week 1",
    body: "Target customer, list and messaging agreed. Sending setup and warm-up start.",
  },
  {
    when: "Week 2",
    body: "Sequences live, replies handled, first conversations.",
  },
];

const ADDONS = [
  { name: "Coached handover", price: "S$500" },
  { name: "Support bundle, one month of support with weekly check-ins and coached handover", price: "S$1,000" },
  { name: "SDR coaching", price: "S$500 per session, or S$1,200 for 3" },
];

export default function OutboundEngineBuildPage() {
  return (
    <main data-testid="outbound-engine-build-page">
      <Seo
        title="Outbound Engine Build"
        description="A working outbound engine for one target customer and one market, live in 2 weeks. S$3,000, built in your own accounts in Singapore."
        path="/outbound-engine-build"
        image="/og-build-2026.jpg"
        imageAlt="A working outbound engine, live in 2 weeks. S$3,000 one-off."
      />
      <PageHero
        label="Outbound Engine Build"
        headline="A working outbound engine, live in 2 weeks."
        subline="For B2B companies that need more of the right conversations, fast."
      />

      <section className="px-6 sm:px-8 pb-20" data-testid="price-block">
        <div className="max-w-4xl mx-auto bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-8 sm:p-10">
          <p className="font-fraunces font-extrabold text-4xl sm:text-5xl text-[#1F1F1C]" data-testid="build-price">
            S$3,000
          </p>
          <ul className="mt-6 space-y-2 text-sm md:text-base text-[#4A463F] leading-relaxed">
            <li>One target customer profile, one market.</li>
            <li>Each extra market S$1,000.</li>
            <li>Payment 50% to start, 50% on go-live.</li>
          </ul>
        </div>
      </section>

      <section className="px-6 sm:px-8 pb-20 sm:pb-24" data-testid="whats-included">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1F1F1C]">
            What's included
          </h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-4">
            {INCLUDED.map((item, i) => (
              <li
                key={item}
                className="flex gap-3 bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-5"
                data-testid={`included-item-${i + 1}`}
              >
                <Check className="w-5 h-5 text-[#C8703A] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span className="text-sm text-[#1F1F1C] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[#3D472D] px-6 sm:px-8 py-20 sm:py-24" data-testid="timeline">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#F4E9D8]">Timeline</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {TIMELINE.map((t) => (
              <div
                key={t.when}
                className="rounded-2xl border border-[#F4E9D8]/20 bg-[#55633F]/50 p-7"
                data-testid={`timeline-${t.when.toLowerCase().replace(" ", "-")}`}
              >
                <p className="font-fraunces font-extrabold text-base md:text-lg text-[#C8703A]">{t.when}</p>
                <p className="mt-3 text-sm text-[#F4E9D8]/75 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 sm:px-8 py-20 sm:py-24" data-testid="add-ons">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1F1F1C]">Add-ons</h2>
          <ul className="mt-10 divide-y divide-[#1F1F1C]/10 border-y border-[#1F1F1C]/10">
            {ADDONS.map((a, i) => (
              <li
                key={a.name}
                className="py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                data-testid={`addon-${i + 1}`}
              >
                <span className="text-sm md:text-base text-[#1F1F1C] pr-6 leading-relaxed">{a.name}</span>
                <span className="font-fraunces font-extrabold text-base text-[#C8703A] whitespace-nowrap">{a.price}</span>
              </li>
            ))}
          </ul>
          <div className="mt-12">
            <AuditCta testId="build-page-cta" />
          </div>
        </div>
      </section>

      <ClosingCta testId="build-closing-cta" />
    </main>
  );
}
